import { useEffect } from 'react';

import { useGateValue, useStatsigClient } from '@coursera-web/statsig';

import {
  ORG_SCOPED_HOME_OVERRIDE_ORG_IDS,
  ORG_SCOPED_HOME_STATSIG_GATE,
} from 'bundles/unified-common/constants/orgScopedHomeStatsig';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useOrgScopedHomeFeature } from 'bundles/unified-common/contexts/OrgScopedHomeFeatureContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import { useUserExperienceMemberships } from 'bundles/unified-common/contexts/UserExperienceMembershipsContext';

/**
 * Determines if the org-scoped unified home experience is enabled.
 *
 * Returns true when EITHER:
 * 1. The user's current org or any program membership org is in ORG_SCOPED_HOME_OVERRIDE_ORG_IDS
 *    (bypasses gate and product feature — used for early-access partners), OR
 * 2. BOTH: the Statsig gate `org-scoped-home` passes AND `enableOrgScopedHome` product feature is enabled.
 *
 * The Statsig exposure is logged whenever the hook returns true (either path),
 * preventing impression pollution for users whose org is not yet enabled in the phased rollout.
 *
 * Reads the product feature from EnterpriseProductFeaturesContext (available on enterprise/org-scoped pages)
 * or from OrgScopedHomeFeatureContext (available inside UnifiedUserExperienceHandler on consumer pages).
 */
export function useIsOrgScopedHome(): boolean {
  const userInOrgScopedRollout = useGateValue(ORG_SCOPED_HOME_STATSIG_GATE, { disableExposureLog: true });
  const { checkGate } = useStatsigClient();
  const features = useEnterpriseProductFeatures();
  const orgScopedHomeFeature = useOrgScopedHomeFeature();
  const thirdPartyOrg = useThirdPartyOrganization();
  const { programMemberships } = useUserExperienceMemberships();

  const featureEnabled = features !== undefined ? features.enableOrgScopedHome ?? false : orgScopedHomeFeature.enabled;

  const isOverrideOrg =
    (thirdPartyOrg != null && ORG_SCOPED_HOME_OVERRIDE_ORG_IDS.has(thirdPartyOrg.id)) ||
    (programMemberships?.some((edge) => {
      const orgId = edge?.node?.program?.naptimeThirdPartyOrganization?.id;
      return orgId != null && ORG_SCOPED_HOME_OVERRIDE_ORG_IDS.has(orgId);
    }) ??
      false);

  const isEnabled = isOverrideOrg || (userInOrgScopedRollout && featureEnabled);

  // Log the Statsig exposure only when both the gate and product feature pass.
  // This prevents polluting analytics with impressions for users whose org
  // is not yet enabled in the phased rollout.
  // checkGate is stable (singleton client reference from StatsigProvider).
  useEffect(() => {
    if (isEnabled) {
      checkGate(ORG_SCOPED_HOME_STATSIG_GATE);
    }
  }, [isEnabled, checkGate]);

  return isEnabled;
}
